import { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';
import clsx from 'clsx';
import toast from 'react-hot-toast';
import DOMPurify from 'dompurify';
import {
  ArrowsPointingInIcon, ArrowsPointingOutIcon, BoldIcon, CheckCircleIcon, ExclamationTriangleIcon,
  LinkIcon, ListBulletIcon, MinusIcon, PaperClipIcon, XMarkIcon,
} from '@heroicons/react/24/outline';

import Button from '../../components/ui/Button';
import ClientCombobox from '../../components/ui/ClientCombobox';
import ConfirmDialog from '../../components/ui/ConfirmDialog';
import { clientMailService, clientService } from '../../services';
import { wrapEmailSrcDoc } from '../../utils/emailClipboard';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const MAX_SUBJECT = 200;
const MAX_MESSAGE = 20000;

const newKey = () => (window.crypto?.randomUUID?.() || `${Date.now()}-${Math.random().toString(36).slice(2)}`).replace(/[^A-Za-z0-9_-]/g, '');

const readBase64 = (file) => new Promise((resolve, reject) => {
  const r = new FileReader();
  r.onload = () => resolve(String(r.result).split(',')[1] || '');
  r.onerror = () => reject(new Error(`Could not read ${file.name}`));
  r.readAsDataURL(file);
});

/** pdf / png / jpg from the name, or from the type a dragged screenshot often has instead. */
function fileExt(file) {
  const name = String(file?.name || '');
  if (/\.(pdf|png|jpe?g)$/i.test(name)) {
    const ext = name.split('.').pop().toLowerCase();
    return ext === 'jpeg' ? 'jpg' : ext;
  }
  if (file?.type === 'image/png') return 'png';
  if (file?.type === 'image/jpeg') return 'jpg';
  if (file?.type === 'application/pdf') return 'pdf';
  return '';
}

/** A pasted screenshot often arrives with no name. Give it one the server can keep. */
function namedFile(file) {
  const ext = fileExt(file);
  if (/\.(pdf|png|jpe?g)$/i.test(file?.name || '')) return file;
  const stamp = new Date().toISOString().slice(0, 19).replace(/[:T]/g, '-');
  return new File([file], `screenshot-${stamp}.${ext || 'png'}`, { type: file.type || 'image/png' });
}

const kb = (n) => (n >= 1024 * 1024 ? `${(n / 1024 / 1024).toFixed(1)} MB` : `${Math.max(1, Math.round(n / 1024))} KB`);
const splitAddresses = (raw) => String(raw || '').split(/[,;\s]+/).map((e) => e.trim().toLowerCase()).filter(Boolean);

/** To / Cc / Bcc as chips, with the location's known addresses offered as you type. */
function RecipientField({ label, values, onChange, known, autoFocus = false, trailing }) {
  const [text, setText] = useState('');
  const [open, setOpen] = useState(false);
  const knownSet = useMemo(() => new Set(known.map((k) => k.email)), [known]);
  const suggestions = known.filter((k) => !values.includes(k.email) && (!text || k.email.includes(text.toLowerCase()) || String(k.name || '').toLowerCase().includes(text.toLowerCase()))).slice(0, 8);

  const commit = (raw = text) => {
    /* A name typed and Enter / Tab: take the first suggestion, not "shifa" as an address. */
    const typed = raw.trim();
    const pick = typed && !/[@,;\s]/.test(typed) ? suggestions[0]?.email : '';
    const add = (pick ? [pick] : splitAddresses(raw)).filter((e) => !values.includes(e));
    if (add.length) onChange([...values, ...add]);
    setText('');
  };

  return (
    <div className="relative flex items-start gap-2 border-b border-surface-100 px-4 py-1.5">
      <span className="w-9 shrink-0 pt-1.5 text-sm text-surface-500">{label}</span>
      <div className="flex min-w-0 flex-1 flex-wrap items-center gap-1">
        {values.map((e) => (
          <span
            key={e}
            className={clsx(
              'inline-flex max-w-full items-center gap-1 rounded-full border px-2 py-0.5 text-xs',
              !EMAIL_RE.test(e) ? 'border-error-300 bg-error-50 text-error-700'
                : knownSet.has(e) ? 'border-surface-200 bg-surface-50 text-dark-800' : 'border-warning-300 bg-warning-50 text-warning-800',
            )}
            title={!EMAIL_RE.test(e) ? 'Not a valid address' : knownSet.has(e) ? 'A known address of this location' : 'Not a known address of this location — you will be asked to confirm it'}
          >
            <span className="truncate">{e}</span>
            <button type="button" aria-label={`Remove ${e}`} className="text-surface-400 hover:text-dark-800" onClick={() => onChange(values.filter((v) => v !== e))}>
              <XMarkIcon className="h-3 w-3" />
            </button>
          </span>
        ))}
        <input
          autoFocus={autoFocus}
          aria-label={label}
          value={text}
          onChange={(e) => { setText(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          onBlur={() => { window.setTimeout(() => setOpen(false), 150); if (text.trim()) commit(); }}
          onKeyDown={(e) => {
            if (['Enter', ',', ';', 'Tab'].includes(e.key) && text.trim()) { e.preventDefault(); commit(); }
            if (e.key === 'Backspace' && !text && values.length) onChange(values.slice(0, -1));
          }}
          onPaste={(e) => {
            const pasted = e.clipboardData.getData('text');
            if (/[,;\s]/.test(pasted.trim())) { e.preventDefault(); commit(pasted); }
          }}
          className="min-w-[8rem] flex-1 border-0 bg-transparent py-1 text-sm text-dark-900 outline-none focus:ring-0"
        />
      </div>
      {trailing}
      {open && suggestions.length > 0 && (
        <ul className="absolute left-14 right-4 top-full z-20 mt-0.5 max-h-56 overflow-y-auto rounded-lg border border-surface-200 bg-white py-1 shadow-lg">
          {suggestions.map((s) => (
            <li key={s.email}>
              <button
                type="button"
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => { onChange([...values, s.email]); setText(''); }}
                className="flex w-full items-center justify-between gap-3 px-3 py-1.5 text-left text-sm hover:bg-surface-50"
              >
                <span className="min-w-0 truncate text-dark-900">{s.name ? <>{s.name} <span className="text-surface-500">{s.email}</span></> : s.email}</span>
                <span className="shrink-0 text-xs text-surface-400">{s.source}</span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/**
 * Client mail → Compose. A familiar compose panel — docked bottom-right on a
 * desktop, full screen on a phone. Full size fills the window with a margin
 * so the right edge is not clipped by the page. The main action is SUBMIT FOR APPROVAL:
 * nothing is sent from here. An admin or DPM coordinator approves it in the
 * Approval Queue (you may approve your own), and it goes the moment they do.
 * The server re-checks everything; this only makes the checks visible early.
 */
export default function ComposePanel({ initialClientId = '', draftId = '', onClose, onSubmitted }) {
  const [requestKey, setRequestKey] = useState(newKey);
  const [clients, setClients] = useState([]);
  const [clientId, setClientId] = useState(initialClientId);
  const [ctx, setCtx] = useState(null);
  const [to, setTo] = useState([]);
  const [cc, setCc] = useState([]);
  const [bcc, setBcc] = useState([]);
  const [showCc, setShowCc] = useState(false);
  const [showBcc, setShowBcc] = useState(false);
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [files, setFiles] = useState([]);
  const [kept, setKept] = useState([]); // a draft's saved attachments: { index, filename, size }
  const [confirmOther, setConfirmOther] = useState(false);
  const [ackOff, setAckOff] = useState(false);
  const [mode, setMode] = useState('write');
  const [previewHtml, setPreviewHtml] = useState('');
  const [busy, setBusy] = useState('');
  const [error, setError] = useState('');
  const [done, setDone] = useState(null);
  const [minimized, setMinimized] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [confirmDiscard, setConfirmDiscard] = useState(false);
  const [dirty, setDirty] = useState(false);
  const bodyRef = useRef(null);
  const fileRef = useRef(null);

  useEffect(() => {
    clientService.list({ all: 1 }).then((r) => setClients(r.data || [])).catch(() => setClients([]));
  }, []);

  /* Reopen a saved draft. */
  useEffect(() => {
    if (!draftId) return;
    clientMailService.get(draftId).then((r) => {
      const d = r.data;
      setRequestKey(String(d.sourceRef || '').replace(/^manual:/, '') || newKey());
      setClientId(String(d.clientId?._id || d.clientId || ''));
      setTo(d.to || []); setCc(d.cc || []); setBcc(d.bcc || []);
      setShowCc(!!d.cc?.length); setShowBcc(!!d.bcc?.length);
      setSubject(d.subject === '(no subject)' ? '' : d.subject || '');
      setMessage(d.message || '');
      setKept(d.attachments || []);
    }).catch((err) => toast.error(err.message || 'Could not open that draft'));
  }, [draftId]);

  useEffect(() => {
    setCtx(null);
    setAckOff(false);
    if (!clientId) return;
    clientMailService.manualContext(clientId)
      .then((r) => {
        setCtx(r.data);
        setTo((cur) => (cur.length || draftId ? cur : (r.data?.recipients?.[0]?.email ? [r.data.recipients[0].email] : [])));
      })
      .catch((err) => setError(err.message || 'Could not load that location'));
  }, [clientId, draftId]);

  const known = useMemo(() => ctx?.recipients || [], [ctx]);
  const knownSet = useMemo(() => new Set(known.map((k) => k.email)), [known]);
  /* Who reviews client mail — offered in Cc / Bcc, and allowed there without confirming. */
  const copyKnown = useMemo(() => [...known, ...(ctx?.team || []).filter((p) => !knownSet.has(p.email))], [known, knownSet, ctx]);
  const teamSet = useMemo(() => new Set((ctx?.team || []).map((p) => p.email)), [ctx]);
  const all = [...to, ...cc, ...bcc];
  const invalid = all.filter((e) => !EMAIL_RE.test(e));
  const unknown = [...new Set(all)].filter((e) => EMAIL_RE.test(e) && !knownSet.has(e) && !(teamSet.has(e) && !to.includes(e)));
  const limits = ctx?.limits || { maxFiles: 3, maxBytes: 5 * 1024 * 1024, types: ['pdf', 'png', 'jpg'] };
  const bytes = files.reduce((n, f) => n + f.size, 0) + kept.reduce((n, a) => n + (a.size || 0), 0);
  const fileCount = files.length + kept.length;

  const touch = (fn) => (...args) => { setDirty(true); setError(''); fn(...args); };

  const addFiles = (list) => {
    const next = [...files];
    for (const raw of Array.from(list || [])) {
      const f = namedFile(raw);
      if (!limits.types.includes(fileExt(f))) { toast.error(`${f.name}: only PDF, PNG or JPG`); continue; }
      if (next.length + kept.length >= limits.maxFiles) { toast.error(`At most ${limits.maxFiles} attachments`); break; }
      if (next.reduce((n, x) => n + x.size, 0) + kept.reduce((n, a) => n + (a.size || 0), 0) + f.size > limits.maxBytes) {
        toast.error('Attachments are limited to 5 MB in total');
        break;
      }
      next.push(f);
    }
    setFiles(next);
    setDirty(true);
  };

  /* Basic formatting the email understands: **bold**, [text](link), "- " bullets. Never raw HTML. */
  const wrap = (before, after = before, placeholder = 'text') => {
    const el = bodyRef.current;
    if (!el) return;
    const { selectionStart: a, selectionEnd: b } = el;
    const sel = message.slice(a, b) || placeholder;
    const next = `${message.slice(0, a)}${before}${sel}${after}${message.slice(b)}`;
    setMessage(next);
    setDirty(true);
    window.requestAnimationFrame(() => { el.focus(); el.setSelectionRange(a + before.length, a + before.length + sel.length); });
  };
  const insertLink = () => {
    const url = window.prompt('Link address (https://…)');
    if (!url) return;
    if (!/^https?:\/\//i.test(url)) { toast.error('Use a full https:// link'); return; }
    wrap('[', `](${url})`, 'link text');
  };
  const bullets = () => {
    const el = bodyRef.current;
    if (!el) return;
    const { selectionStart: a, selectionEnd: b } = el;
    const start = message.lastIndexOf('\n', a - 1) + 1;
    const block = message.slice(start, b || a).split('\n').map((l) => (l.startsWith('- ') ? l : `- ${l}`)).join('\n');
    setMessage(`${message.slice(0, start)}${block}${message.slice(b || a)}`);
    setDirty(true);
  };

  const payload = async () => ({
    requestKey,
    clientId,
    to, cc, bcc,
    subject: subject.trim(),
    message,
    keepAttachments: kept.map((a) => a.index),
    attachments: await Promise.all(files.map(async (f) => ({ filename: f.name, contentBase64: await readBase64(f) }))),
    confirmOtherRecipient: confirmOther,
    acknowledgedNotificationsOff: ackOff,
  });

  const showPreview = async () => {
    if (!subject.trim() || !message.trim()) { setError('Write a subject and a message to preview it'); return; }
    setBusy('preview');
    try {
      const r = await clientMailService.manualPreview({ clientId, subject: subject.trim(), message });
      setPreviewHtml(wrapEmailSrcDoc(DOMPurify.sanitize(r.data?.html || '', { WHOLE_DOCUMENT: true })));
      setMode('preview');
    } catch (err) {
      setError(err.message || 'Could not build the preview');
    } finally {
      setBusy('');
    }
  };

  const saveDraft = async () => {
    if (!clientId) { setError('Choose a practice location first'); return; }
    setBusy('draft');
    try {
      const r = await clientMailService.manualDraft(await payload());
      setFiles([]);
      const fresh = await clientMailService.get(r.data.id);
      setKept(fresh.data?.attachments || []);
      setDirty(false);
      toast.success('Draft saved — only you can see it');
      onSubmitted?.();
    } catch (err) {
      setError(err.message || 'Could not save the draft');
    } finally {
      setBusy('');
    }
  };

  const submit = async () => {
    setError('');
    if (!clientId) { setError('Choose a practice location'); return; }
    if (!to.length) { setError('Add at least one recipient'); return; }
    if (invalid.length) { setError(`Not a valid address: ${invalid.join(', ')}`); return; }
    if (!subject.trim()) { setError('Subject is required'); return; }
    if (!message.trim()) { setError('Message is required'); return; }
    if (unknown.length && !confirmOther) { setError('Confirm the highlighted addresses belong to this location'); return; }
    if (ctx?.notificationsOff?.length && !ackOff) { setError('Confirm you want to write while their notifications are off'); return; }
    setBusy('submit');
    try {
      const r = await clientMailService.manualSubmit(await payload());
      setDone(r.data);
      setDirty(false);
      onSubmitted?.();
    } catch (err) {
      setError(err.message || 'Could not submit it');
    } finally {
      setBusy('');
    }
  };

  const close = () => (dirty && !done ? setConfirmDiscard(true) : onClose());

  return createPortal(
    <>
      <div
        role="dialog"
        aria-label="Compose email"
        className={clsx(
          'fixed z-[80] flex flex-col overflow-hidden border border-surface-300 bg-white shadow-2xl',
          minimized && 'bottom-0 right-4 h-auto w-80 rounded-t-xl',
          !minimized && !expanded && 'inset-0 sm:inset-auto sm:bottom-0 sm:right-4 sm:h-[min(42rem,88vh)] sm:w-[36rem] sm:rounded-t-xl',
          !minimized && expanded && 'inset-3 rounded-xl sm:inset-6',
        )}
      >
        <div className="flex shrink-0 cursor-default items-center justify-between gap-2 bg-dark-900 px-4 py-2.5 text-white">
          <button type="button" className="min-w-0 truncate text-left text-sm font-medium" onClick={() => setMinimized((m) => !m)}>
            {done ? 'Submitted for approval' : subject.trim() || (draftId ? 'Draft' : 'New message')}
          </button>
          <div className="flex shrink-0 items-center gap-1 text-white/70">
            <button type="button" aria-label={minimized ? 'Restore' : 'Minimise'} className="hidden rounded p-1 hover:bg-white/10 hover:text-white sm:block" onClick={() => setMinimized((m) => !m)}>
              <MinusIcon className="h-4 w-4" />
            </button>
            <button type="button" aria-label={expanded ? 'Exit full size' : 'Full size'} className="hidden rounded p-1 hover:bg-white/10 hover:text-white sm:block" onClick={() => { setExpanded((x) => !x); setMinimized(false); }}>
              {expanded ? <ArrowsPointingInIcon className="h-4 w-4" /> : <ArrowsPointingOutIcon className="h-4 w-4" />}
            </button>
            <button type="button" aria-label="Close" className="rounded p-1 hover:bg-white/10 hover:text-white" onClick={close}>
              <XMarkIcon className="h-4 w-4" />
            </button>
          </div>
        </div>

        {!minimized && done && (
          <div className="flex flex-1 flex-col items-center justify-center gap-3 p-8 text-center">
            <CheckCircleIcon className="h-12 w-12 text-success-500" />
            <h3 className="text-lg font-semibold text-dark-900">Submitted for approval</h3>
            <p className="max-w-sm text-sm text-surface-600">
              Nothing has been sent yet. Once an admin or DPM coordinator approves it in the Approval Queue — you can
              approve your own — it goes straight away through the client SMTP.
            </p>
            <div className="flex gap-2">
              <Link to={`/client-mail/approvals?message=${done.id}`} onClick={onClose} className="btn-primary px-3 py-1.5 text-sm">Open in Approval Queue</Link>
              <Button type="button" variant="secondary" size="sm" onClick={onClose}>Close</Button>
            </div>
          </div>
        )}

        {!minimized && !done && (
          <>
            <div className="min-h-0 flex-1 overflow-y-auto">
              <div className="flex items-center gap-2 border-b border-surface-100 px-4 py-1.5">
                <span className="w-9 shrink-0 text-sm text-surface-500">For</span>
                <div className="min-w-0 flex-1">
                  <ClientCombobox clients={clients} value={clientId} placeholder="Practice location" onChange={touch((id) => { setClientId(id); setTo([]); })} />
                </div>
              </div>
              <div className="flex items-center gap-2 border-b border-surface-100 px-4 py-2 text-sm">
                <span className="w-9 shrink-0 text-surface-500">From</span>
                <span className="truncate text-dark-800" title="The client SMTP identity — set in Settings → API Keys → Email">{ctx?.from || 'Client SMTP'}</span>
              </div>
              <RecipientField
                label="To"
                values={to}
                onChange={touch(setTo)}
                known={known}
                autoFocus={!!initialClientId}
                trailing={(
                  <div className="flex shrink-0 gap-2 pt-1.5 text-xs font-medium text-surface-500">
                    {!showCc && <button type="button" className="hover:text-dark-800" onClick={() => setShowCc(true)}>Cc</button>}
                    {!showBcc && <button type="button" className="hover:text-dark-800" onClick={() => setShowBcc(true)}>Bcc</button>}
                  </div>
                )}
              />
              {showCc && <RecipientField label="Cc" values={cc} onChange={touch(setCc)} known={copyKnown} />}
              {showBcc && <RecipientField label="Bcc" values={bcc} onChange={touch(setBcc)} known={copyKnown} />}
              <div className="border-b border-surface-100 px-4">
                <input
                  aria-label="Subject"
                  placeholder="Subject"
                  maxLength={MAX_SUBJECT}
                  value={subject}
                  onChange={(e) => touch(setSubject)(e.target.value)}
                  className="w-full border-0 bg-transparent py-2.5 text-sm text-dark-900 outline-none focus:ring-0"
                />
              </div>

              {ctx?.testRecipient && (
                <p className="mx-4 mt-3 rounded-md bg-warning-50 px-3 py-2 text-xs text-warning-800">
                  Test mode: once approved it goes only to <strong>{ctx.testRecipient}</strong>, not to the practice.
                </p>
              )}
              {ctx?.cannotSend && (
                <p className="mx-4 mt-3 rounded-md bg-error-50 px-3 py-2 text-xs text-error-700">{ctx.cannotSend} You can submit it; it cannot be approved until this is fixed.</p>
              )}

              {mode === 'write' ? (
                <textarea
                  ref={bodyRef}
                  aria-label="Message"
                  placeholder={'Hi,\n\nWrite your message…'}
                  maxLength={MAX_MESSAGE}
                  value={message}
                  onChange={(e) => touch(setMessage)(e.target.value)}
                  onPaste={(e) => { if (e.clipboardData?.files?.length) { e.preventDefault(); addFiles(e.clipboardData.files); } }}
                  onDrop={(e) => { if (e.dataTransfer?.files?.length) { e.preventDefault(); addFiles(e.dataTransfer.files); } }}
                  className={clsx('block w-full resize-none border-0 px-4 py-3 text-sm leading-relaxed text-dark-900 outline-none focus:ring-0', expanded ? 'min-h-[45vh]' : 'min-h-[14rem]')}
                />
              ) : (
                <iframe title="Email preview" srcDoc={previewHtml} sandbox="" className="block h-[50vh] w-full border-0 bg-surface-50" />
              )}

              {(fileCount > 0) && (
                <ul className="space-y-1.5 px-4 pb-3">
                  {kept.map((a) => (
                    <li key={`k${a.index}`} className="flex items-center justify-between gap-2 rounded-md border border-surface-200 bg-surface-50 px-3 py-1.5 text-xs">
                      <span className="flex min-w-0 items-center gap-1.5"><PaperClipIcon className="h-3.5 w-3.5 shrink-0 text-surface-400" /><span className="truncate">{a.filename}</span><span className="text-surface-400">{kb(a.size || 0)}</span></span>
                      <button type="button" aria-label={`Remove ${a.filename}`} className="text-surface-400 hover:text-error-600" onClick={() => { setKept(kept.filter((k) => k.index !== a.index)); setDirty(true); }}><XMarkIcon className="h-4 w-4" /></button>
                    </li>
                  ))}
                  {files.map((f, i) => (
                    <li key={`${f.name}-${i}`} className="flex items-center justify-between gap-2 rounded-md border border-surface-200 bg-surface-50 px-3 py-1.5 text-xs">
                      <span className="flex min-w-0 items-center gap-1.5">
                        <PaperClipIcon className="h-3.5 w-3.5 shrink-0 text-surface-400" />
                        <span className="truncate">{f.name}</span>
                        <span className="text-surface-400">{kb(f.size)}</span>
                      </span>
                      <button type="button" aria-label={`Remove ${f.name}`} className="text-surface-400 hover:text-error-600" onClick={() => { setFiles(files.filter((_, j) => j !== i)); setDirty(true); }}><XMarkIcon className="h-4 w-4" /></button>
                    </li>
                  ))}
                  <li className="text-[11px] text-surface-400">{fileCount}/{limits.maxFiles} files · {kb(bytes)} of 5 MB</li>
                </ul>
              )}

              {(unknown.length > 0 || ctx?.notificationsOff?.length > 0) && (
                <div className="space-y-2 px-4 pb-3">
                  {unknown.length > 0 && (
                    <label className="flex items-start gap-2 rounded-md border border-warning-200 bg-warning-50 px-3 py-2 text-xs text-warning-800">
                      <input type="checkbox" className="mt-0.5" checked={confirmOther} onChange={(e) => setConfirmOther(e.target.checked)} />
                      <span><strong>{unknown.join(', ')}</strong> {unknown.length === 1 ? 'is' : 'are'} not a known address of this location. I confirm {unknown.length === 1 ? 'it belongs' : 'they belong'} to this practice.</span>
                    </label>
                  )}
                  {ctx?.notificationsOff?.length > 0 && (
                    <label className="flex items-start gap-2 rounded-md border border-surface-200 bg-surface-50 px-3 py-2 text-xs text-surface-700">
                      <input type="checkbox" className="mt-0.5" checked={ackOff} onChange={(e) => setAckOff(e.target.checked)} />
                      <span>
                        <ExclamationTriangleIcon className="mr-1 inline h-3.5 w-3.5 text-warning-600" />
                        This location has email notifications switched off ({ctx.notificationsOff.slice(0, 4).join(', ')}{ctx.notificationsOff.length > 4 ? ` and ${ctx.notificationsOff.length - 4} more` : ''}).
                        Automated mail stays off; I still want to send this message. The reviewer will see this.
                      </span>
                    </label>
                  )}
                </div>
              )}
            </div>

            {error && <p role="alert" className="shrink-0 border-t border-error-100 bg-error-50 px-4 py-2 text-xs text-error-700">{error}</p>}

            <div className="flex shrink-0 flex-wrap items-center gap-1.5 border-t border-surface-100 px-3 py-2.5">
              <Button type="button" size="sm" onClick={submit} disabled={!!busy}>
                {busy === 'submit' ? 'Submitting…' : 'Submit for approval'}
              </Button>
              <div className="flex items-center gap-0.5 text-surface-500">
                {mode === 'write' && (
                  <>
                    <button type="button" title="Bold" aria-label="Bold" className="rounded p-1.5 hover:bg-surface-100 hover:text-dark-800" onClick={() => wrap('**')}><BoldIcon className="h-4 w-4" /></button>
                    <button type="button" title="Link" aria-label="Insert link" className="rounded p-1.5 hover:bg-surface-100 hover:text-dark-800" onClick={insertLink}><LinkIcon className="h-4 w-4" /></button>
                    <button type="button" title="Bulleted list" aria-label="Bulleted list" className="rounded p-1.5 hover:bg-surface-100 hover:text-dark-800" onClick={bullets}><ListBulletIcon className="h-4 w-4" /></button>
                  </>
                )}
                <button type="button" title="Attach PDF, PNG or JPG" aria-label="Attach files" className="rounded p-1.5 hover:bg-surface-100 hover:text-dark-800" onClick={() => fileRef.current?.click()} disabled={fileCount >= limits.maxFiles}>
                  <PaperClipIcon className="h-4 w-4" />
                </button>
                <input ref={fileRef} type="file" multiple accept=".pdf,.png,.jpg,.jpeg,application/pdf,image/png,image/jpeg" className="hidden" onChange={(e) => { addFiles(e.target.files); e.target.value = ''; }} />
              </div>
              <button type="button" className="text-xs font-medium text-brand-600 hover:text-brand-700" onClick={() => (mode === 'write' ? showPreview() : setMode('write'))} disabled={!!busy}>
                {mode === 'write' ? (busy === 'preview' ? 'Building…' : 'Preview') : 'Back to editing'}
              </button>
              <span className="ml-auto text-[11px] text-surface-400">{message.length.toLocaleString()} / {MAX_MESSAGE.toLocaleString()}</span>
              <Button type="button" size="sm" variant="ghost" className="!px-2" onClick={saveDraft} disabled={!!busy}>{busy === 'draft' ? 'Saving…' : 'Save draft'}</Button>
              <Button type="button" size="sm" variant="ghost" className="!px-2" onClick={close} disabled={!!busy}>Discard</Button>
            </div>
          </>
        )}
      </div>
      <ConfirmDialog
        open={confirmDiscard}
        title="Discard this email?"
        message="What you wrote since the last save will be lost. A saved draft stays in Client mail → My drafts."
        confirmLabel="Discard"
        cancelLabel="Keep writing"
        onCancel={() => setConfirmDiscard(false)}
        onConfirm={() => { setConfirmDiscard(false); onClose(); }}
      />
    </>,
    document.body,
  );
}
